import { Box, Group, List, SimpleGrid, Text, ThemeIcon } from '@mantine/core';
import { Info, Sparkles } from 'lucide-react';
import type { ReactElement } from 'react';
import StatBand from '@/design/StatBand';
import { SurfaceCard } from '@/design/SurfaceCard';
import { formatCompact } from '@/shared/analytics/MetricFormatting';
import InsightsWastedTokenChart from './InsightsWastedTokenChart';
import type { CohortStanding, EfficiencyImpact } from './InsightsTypes';

interface InsightsEfficiencySectionProps {
    efficiency: EfficiencyImpact;
    /** The selected peer cohort (from the global cohort selector) — backs the per-tile rank badges. */
    cohort: CohortStanding | null;
    /** The selected window — the productive-vs-wasted trend is plotted across `[periodStartIso, periodEndIso]`. */
    periodStartIso: string;
    periodEndIso: string;
}

/** Friendly names for the tools that don't report token counts, so the empty state reads naturally. */
const TOOL_LABELS: Record<string, string> = {
    'm365-copilot': 'Microsoft Copilot',
    'github-copilot': 'GitHub Copilot',
};

function toolLabel(category: string): string {
    return TOOL_LABELS[category] ?? category;
}

/** One-decimal ratio (turns/topic, topics/session) — small numbers where `formatCompact` would over-round. */
function ratio(value: number): string {
    return value.toFixed(1);
}

/** A per-tile cohort-rank pill ("#3 of 11"), or undefined when the metric isn't rankable. */
function rankBadge(rank: number | null, size: number): string | undefined {
    return rank != null ? `#${rank} of ${size}` : undefined;
}

/** Usage Efficiency — token-based habits (no dollars): headline KPIs, topic focus, model fit, each ranked in your cohort. */
export default function InsightsEfficiencySection({ efficiency, cohort, periodStartIso, periodEndIso }: InsightsEfficiencySectionProps): ReactElement {
    // Chrome speaks brand — section icons/actions no longer carry a per-section hue.
    const tokens = { solid: 'var(--bm-color-brand-accent)', soft: 'var(--bm-color-brand-soft)', text: 'var(--bm-color-brand-accent)' };

    if (efficiency.totalTokens === 0) {
        const tools = [...new Set(efficiency.comparison.map((c) => toolLabel(c.category)))];
        return (
            <SurfaceCard $padding="lg">
                <Group gap={12} align="flex-start" wrap="nowrap">
                    <ThemeIcon size={32} radius="xl" style={{ background: tokens.soft, color: tokens.text }}>
                        <Info size={17} />
                    </ThemeIcon>
                    <Box>
                        <Text fw={600} mb={4}>
                            No token-metered usage in this period
                        </Text>
                        <Text size="sm" c="dimmed" style={{ lineHeight: 1.55 }}>
                            {tools.length > 0 ? `The tools used here (${tools.join(', ')}) don't report token counts` : "The tools used here don't report token counts"},
                            so token efficiency can't be measured. Widen the date range to include token-metered tools.
                        </Text>
                    </Box>
                </Group>
            </SurfaceCard>
        );
    }

    const ranks = cohort?.efficiencyRanks ?? null;
    const size = cohort?.size ?? 0;

    return (
        <Box>
            <StatBand
                stats={[
                    {
                        label: 'Total tokens',
                        value: formatCompact(efficiency.totalTokens),
                        hint: `${formatCompact(efficiency.inputTokens)} in / ${formatCompact(efficiency.outputTokens)} out`,
                        ...(ranks ? { badge: rankBadge(ranks.totalTokens, size) } : {}),
                    },
                    {
                        label: 'Terms / topic',
                        value: ratio(efficiency.termsPerTopic),
                        info: 'Turns per distinct topic (task category) — how much back-and-forth a single topic takes. Lower is tighter.',
                        ...(ranks ? { badge: rankBadge(ranks.termsPerTopic, size) } : {}),
                    },
                    {
                        label: 'Session drift',
                        value: ratio(efficiency.topicsPerSession),
                        info: 'Distinct topics a typical session touches. ~1.0 is a focused session; higher means sessions wander across unrelated topics, re-sending context on every pivot.',
                        ...(ranks ? { badge: rankBadge(ranks.topicsPerSession, size) } : {}),
                    },
                    {
                        label: 'Model fit',
                        value: `${efficiency.modelFit}%`,
                        info: 'Share of turns where the model tier matched the task — premium models on hard tasks, standard models on easy ones.',
                        ...(ranks ? { badge: rankBadge(ranks.modelFit, size) } : {}),
                    },
                    {
                        label: 'Est. wasted tokens',
                        value: formatCompact(efficiency.estimatedWastedTokens),
                        hint: `${efficiency.wastedTokenRate}% of total`,
                        info: 'Estimated refinement overhead — a heuristic share of tokens from multi-turn (non-one-shot) sessions, i.e. re-work rather than first-try answers. An estimate, not a billed figure.',
                        ...(ranks ? { badge: rankBadge(ranks.wastedTokens, size) } : {}),
                    },
                ]}
            />
            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md" mt="md">
                <SurfaceCard $padding="md">
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb="sm" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                        Tokens — productive vs wasted
                    </Text>
                    <InsightsWastedTokenChart trend={efficiency.wastedTrend} startIso={periodStartIso} endIso={periodEndIso} />
                </SurfaceCard>
                <SurfaceCard $padding="md">
                    <Group gap={6} mb="sm">
                        <Sparkles size={13} color={tokens.solid} />
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                            Recommended actions
                        </Text>
                    </Group>
                    <List
                        spacing="md"
                        icon={
                            <ThemeIcon size={18} radius="xl" style={{ background: tokens.soft, color: tokens.text }}>
                                <Box w={6} h={6} style={{ background: tokens.solid, borderRadius: '50%' }} />
                            </ThemeIcon>
                        }
                    >
                        {efficiency.recommendedActions.map((action) => (
                            <List.Item key={action}>
                                <Text size="sm" style={{ lineHeight: 1.55 }}>
                                    {action}
                                </Text>
                            </List.Item>
                        ))}
                    </List>
                </SurfaceCard>
            </SimpleGrid>
        </Box>
    );
}
